import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Check, X, Loader2, AlertTriangle } from "lucide-react";
import type { GenerateRationaleResponse, KeywordAdjustment, UnfulfilledLever } from "@/types/ab-tests";
import { UnfulfilledLeversDisclosure } from "./UnfulfilledLeversDisclosure";

interface RationaleDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rationale: GenerateRationaleResponse | null;
  isLoading?: boolean;
  keywordAdjustment?: KeywordAdjustment | null;
  unfulfilledLevers?: UnfulfilledLever[] | null;
}

export function RationaleDrawer({
  open,
  onOpenChange,
  rationale,
  isLoading = false,
  keywordAdjustment = null,
  unfulfilledLevers = null,
}: RationaleDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[480px] bg-card border-border p-0 flex flex-col gap-0 overflow-hidden"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <SheetTitle>Signal AI Rationale</SheetTitle>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Analyzing generation decisions...</p>
            </div>
          )}

          {!isLoading && !rationale && (
            <div className="text-center py-12">
              <p className="text-sm text-muted-foreground">Generate Variant B content first to see the AI rationale.</p>
            </div>
          )}

          {!isLoading && rationale && (
            <>
              <UnfulfilledLeversDisclosure levers={unfulfilledLevers} />

              {/* Keyword adjustment disclosure */}
              {keywordAdjustment && (
                <div
                  data-testid="keyword-adjustment-disclosure"
                  className="flex items-start gap-2.5 rounded-lg border border-status-warning-mid bg-status-warning-soft px-3 py-3"
                >
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-status-warning-strong" strokeWidth={2.5} />
                  <p className="text-sm font-medium text-foreground">
                    Requested keyword &quot;{keywordAdjustment.requested_keyword}&quot; was adapted, not inserted verbatim
                  </p>
                </div>
              )}

              {/* Summary */}
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Summary</p>
                <p className="text-sm leading-relaxed text-foreground">{rationale.summary}</p>
              </div>

              {/* Signals used */}
              {rationale.signals.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Signals applied</p>
                  <div className="space-y-1.5">
                    {rationale.signals.map((signal, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                        <span className="text-foreground">{signal}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Phrase changes */}
              {rationale.phraseChanges.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Phrase-level changes</p>
                  <div className="space-y-3">
                    {rationale.phraseChanges.map((change, idx) => (
                      <div key={idx} className="rounded-lg border border-border p-3 space-y-2">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="bg-destructive/10 text-destructive px-2 py-0.5 rounded-md line-through">{change.from}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                          <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-md">{change.to}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{change.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preserved */}
              {rationale.preserved.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Preserved fragments</p>
                  <div className="space-y-1">
                    {rationale.preserved.map((fragment, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs">
                        <Check className="h-3.5 w-3.5 text-trend-positive mt-0.5 shrink-0" />
                        <span className="text-foreground">{fragment}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Dropped */}
              {rationale.dropped.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Dropped fragments</p>
                  <div className="space-y-1">
                    {rationale.dropped.map((fragment, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs">
                        <X className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                        <span className="text-muted-foreground">{fragment}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-border px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
            Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
